/**
 * WP-2 H3 — Reusable step-reaction runner.
 *
 * Wraps the shared `useStepReactions` dispatcher and renders an ActionExecutor
 * per run_action reaction, so listener reactions run identically on page types
 * that are not the popup-oriented FunnelExperience (LandingPage,
 * PostPurchasePage). Navigation reactions are delegated to the host page's
 * accept/decline/go_to_step handlers.
 */

import { useEffect, useRef, useState } from 'react';
import { useFunnelContext } from '../funnel-provider';
import type {
  FunnelAddToCartCallback,
  FunnelOffer,
  RuntimeStepAction,
  StepListener,
} from '../types';
import { ActionExecutor } from './ActionExecutor';
import { useStepReactions } from './use-step-reactions';
import type { TssEventEnvelope, UIEventPayload } from '../events/envelope';
import { markListenerFired, resolveListenerLifecycle } from './listener-lifecycle';
import type { SemanticResultOptions } from '../handlers/types';

export interface StepReactionsProps {
  stepId: string;
  listeners: StepListener[] | undefined;
  triggerLifecycle?: string;
  onAccept: () => void | boolean | Promise<void | boolean>;
  onDecline: () => void | boolean | Promise<void | boolean>;
  onGoToStep?: (
    targetStepId: string | undefined,
    listener: StepListener
  ) => void | boolean | Promise<void | boolean>;
  /** Forwarded to the reaction ActionExecutor for commerce/offer run_actions. */
  onAddToCart?: FunnelAddToCartCallback;
  onOfferAccept?: (
    offerId: string,
    options?: SemanticResultOptions
  ) => void | boolean | Promise<void | boolean>;
  onOfferDecline?: (
    offerId: string,
    options?: SemanticResultOptions
  ) => void | boolean | Promise<void | boolean>;
  fallbackOffer?: Partial<FunnelOffer> | null;
  /** Forwarded to ActionExecutor to hold cart-bound reactions during rollout. */
  commerceEnabled?: boolean;
}

export function StepReactions({
  stepId,
  listeners,
  triggerLifecycle = 'session',
  onAccept,
  onDecline,
  onGoToStep,
  onAddToCart,
  onOfferAccept,
  onOfferDecline,
  fallbackOffer,
  commerceEnabled,
}: StepReactionsProps) {
  const { eventBus } = useFunnelContext();
  const [reactionActions, setReactionActions] = useState<
    Array<{
      key: string;
      action: RuntimeStepAction;
      hopCount: number;
      listener: StepListener;
      intentEnvelope: TssEventEnvelope<UIEventPayload>;
    }>
  >([]);
  const inFlightListenerKeysRef = useRef(new Set<string>());

  // Reset the reaction queue whenever the step changes.
  useEffect(() => {
    setReactionActions([]);
    inFlightListenerKeysRef.current.clear();
  }, [stepId]);

  useStepReactions({
    eventBus,
    stepId,
    listeners,
    triggerLifecycle,
    handlers: {
      onAccept,
      onDecline,
      onGoToStep: onGoToStep ?? (() => {}),
      onRunAction: (action, listener, hopCount, intentEnvelope) => {
        const listenerKey = `${stepId}:${listener.id ?? listener.eventName}`;
        if (inFlightListenerKeysRef.current.has(listenerKey)) {
          return;
        }
        inFlightListenerKeysRef.current.add(listenerKey);
        setReactionActions((prev) => [
          ...prev,
          {
            key: `${listener.id ?? listener.eventName}-${intentEnvelope.metadata.eventId}`,
            action,
            hopCount,
            listener,
            intentEnvelope,
          },
        ]);
      },
    },
  });

  if (reactionActions.length === 0) return null;

  return (
    <>
      {reactionActions.map(({ key, action, hopCount, listener, intentEnvelope }) => (
        <ActionExecutor
          key={key}
          actions={[action]}
          hopCount={hopCount}
          stepId={stepId}
          triggerLifecycle="unlimited"
          onAddToCart={onAddToCart}
          onOfferAccept={onOfferAccept}
          onOfferDecline={onOfferDecline}
          fallbackOffer={fallbackOffer}
          commerceEnabled={commerceEnabled}
          intentEnvelope={intentEnvelope}
          onActionSucceeded={() => {
            markListenerFired(
              stepId,
              listener.id,
              resolveListenerLifecycle(listener.config, triggerLifecycle)
            );
          }}
          onActionSettled={() => {
            inFlightListenerKeysRef.current.delete(
              `${stepId}:${listener.id ?? listener.eventName}`
            );
          }}
        />
      ))}
    </>
  );
}
